import type { MapMetadata, MapWithImages, Stats } from '@/types/api';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

class APIError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'APIError';
  }
}

async function fetchAPI<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  try {
    const response = await fetch(url, options);

    if (!response.ok) {
      throw new APIError(
        response.status,
        `HTTP error! status: ${response.status}`
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new Error(`Failed to fetch from ${endpoint}: ${error}`);
  }
}

export const api = {
  // Get database statistics
  getStats: (options?: RequestInit) => fetchAPI<Stats>('/stats', options),

  // List maps with pagination
  getMaps: (skip = 0, limit = 100, options?: RequestInit) =>
    fetchAPI<MapMetadata[]>(`/maps?skip=${skip}&limit=${limit}`, options),

  // Get random maps
  getRandomMaps: (limit = 12, options?: RequestInit) =>
    fetchAPI<MapMetadata[]>(`/maps/random?limit=${limit}`, options),

  // Get specific map with images
  getMap: (id: number, options?: RequestInit) =>
    fetchAPI<MapWithImages>(`/maps/${id}`, options),

  // Search maps
  searchMaps: (query: string, limit = 50, options?: RequestInit) =>
    fetchAPI<MapMetadata[]>(`/search?q=${encodeURIComponent(query)}&limit=${limit}`, options),

  // Get image URL
  getImageURL: (imageId: number) =>
    `${API_BASE_URL}/images/${imageId}`,

  // Get thumbnail URL
  getThumbnailURL: (imageId: number) =>
    `${API_BASE_URL}/images/${imageId}/thumbnail`,
};

export { APIError };
